import { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';

export const useNotificationManager = (setCartNotification, setWishlistNotification) => {
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      return;
    }
  }, []);

  useEffect(() => {
    // Prevent duplicate toasts when multiple rapid events fire
    const lastToastKeyRef = { key: null, time: 0 };

    // Handle cart notifications
    const handleCartNotification = (event) => {
      try {
        const { subType, message, item, variantCount, variants } = event.detail || {};
      const isMobile = window.innerWidth < 768;
      const toastDuration = isMobile ? 2800 : 3800;

      // Build a stable key to de-dup within a short window
      const baseKey = `${subType || 'add'}:${item?.id || item?._id || 'unknown'}:${item?.variantId || 'na'}`;
      const now = Date.now();
      if (lastToastKeyRef.key === baseKey && now - lastToastKeyRef.time < 1500) {
        return; // skip duplicate burst
      }
      lastToastKeyRef.key = baseKey;
      lastToastKeyRef.time = now;

      // Compute friendly variant summary if available
      const count = Number(variantCount || (Array.isArray(variants) ? variants.length : 0)) || 0;
      const finalMessage = (() => {
        if (subType === 'remove') return message || 'Removed from cart';
        if (count > 1) return `Added ${count} variants to cart`;
        // Fall back to provided message or a default
        return message || 'Added to cart';
      })();

      toast.success(finalMessage, { position: 'top-center', duration: toastDuration });

      // Desktop mini-preview card
      if (!isMobile) {
        setWishlistNotification(null);
        const preview = {
          ...(item || {}),
          customMessage: finalMessage,
          id: 'custom',
          variantCount: count
        };
        setCartNotification(preview);
        setTimeout(() => setCartNotification(null), 2800);
      }
      // Mobile only shows toast
      } catch (err) {
        console.error('[NotificationManager] cartNotification error', err, event);
      }
    };

    // Listen for cart notification events
    window.addEventListener('cartNotification', handleCartNotification);
    return () => window.removeEventListener('cartNotification', handleCartNotification);
  }, [setCartNotification, setWishlistNotification]);

  // Handle wishlist notifications
  useEffect(() => {
    const handleWishlistNotification = (event) => {
      try {
        const { type, item } = event.detail || {};
      const isMobile = window.innerWidth < 768;
      const toastDuration = isMobile ? 3000 : 4000;
      
      // Show toast and mini-preview for wishlist actions
      if (type === 'add') {
        toast.success("Added to Wishlist", { position: "top-center", duration: toastDuration });
        if (!isMobile) {
          setCartNotification(null);
          setWishlistNotification(item);
          setTimeout(() => setWishlistNotification(null), 3000);
        }
      } else if (type === 'remove') {
        toast.success("Removed from Wishlist", { position: "top-center", duration: toastDuration });
        if (!isMobile) {
          setWishlistNotification(null);
        }
      }
      } catch (err) {
        console.error('[NotificationManager] wishlistNotification error', err, event);
      }
    };

    // Listen for wishlist notification events
    window.addEventListener('wishlistNotification', handleWishlistNotification);
    return () => window.removeEventListener('wishlistNotification', handleWishlistNotification);
  }, [setCartNotification, setWishlistNotification]);
};

export default useNotificationManager;